import { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { FileText, Eye, Code2, Save, RotateCcw, Printer, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { render, printDocument } from '../utils/print.js';
import { PLANTILLAS_DEFAULT, TIPOS_PLANTILLA } from '../data/plantillasDefault.js';
import { useAuth } from '../contexts/AuthContext';

// ── Datos de muestra para preview ────────────────────────────────────────────
const DATOS_MUESTRA = {
  venta: {
    numero: '0042', fecha: '24/05/2026', cliente: 'Juan García', forma_pago: 'Efectivo',
    usuario: 'admin', subtotal_fmt: '$18.500,00', descuento_fmt: '$500,00', total_fmt: '$18.000,00',
    items: [
      { producto: 'Pollo entero', cantidad: '2', unidad: 'kg', precio_unitario: '$4.500,00', subtotal: '$9.000,00' },
      { producto: 'Pechuga sin hueso', cantidad: '1', unidad: 'kg', precio_unitario: '$5.500,00', subtotal: '$5.500,00' },
      { producto: 'Menudencias', cantidad: '1', unidad: 'kg', precio_unitario: '$4.000,00', subtotal: '$4.000,00' },
    ],
  },
  compra: {
    id: '18', fecha: '24/05/2026', proveedor: 'Granja El Ombú', usuario: 'admin',
    notas: 'Entrega en depósito central', total_fmt: '$45.000,00',
    items: [
      { producto: 'Pollo entero', cantidad: '10', unidad: 'kg', precio_unitario: '$4.000,00', subtotal: '$40.000,00' },
      { producto: 'Pollo trozado', cantidad: '2', unidad: 'kg', precio_unitario: '$2.500,00', subtotal: '$5.000,00' },
    ],
  },
  pago: {
    id: '7', fecha: '24/05/2026', proveedor: 'Granja El Ombú', forma_pago: 'Transferencia bancaria',
    referencia: 'TRF-20240524-001', monto_fmt: '$45.000,00', usuario: 'admin', notas: '—',
  },
  cobranza: {
    id: '15', fecha: '24/05/2026', cliente: 'Juan García', forma_pago: 'Cheque',
    referencia: 'CHQ-0012345', monto_fmt: '$18.000,00', usuario: 'admin', notas: '—',
  },
};

// ── Variable pill ─────────────────────────────────────────────────────────────
function VarPill({ name }) {
  const isArray = name.endsWith('[]');
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-mono text-[10px] font-semibold
      ${isArray
        ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-500/30'
        : 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 ring-1 ring-primary-200 dark:ring-primary-500/20'
      }`}
    >
      {isArray ? `{{#each ${name.slice(0, -2)}}}` : `{{${name}}}`}
    </span>
  );
}

// ── Preview ───────────────────────────────────────────────────────────────────
function Preview({ html, tipo }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current) return;
    const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    const rendered = render(html, DATOS_MUESTRA[tipo] ?? {});
    doc.open();
    doc.write(rendered);
    doc.close();
  }, [html, tipo]);

  return (
    <iframe
      ref={iframeRef}
      title="Vista previa"
      className="w-full h-full border-0 rounded-b-xl bg-white"
      sandbox="allow-same-origin"
    />
  );
}

// ── Page principal ────────────────────────────────────────────────────────────
export default function Plantillas() {
  const { can } = useAuth();
  const canEdit = can('plantillas.gestionar');

  const [tipoActivo, setTipoActivo] = useState(TIPOS_PLANTILLA[0].tipo);
  const [htmls, setHtmls]           = useState({});   // tipo → html editado
  const [saved, setSaved]           = useState({});    // tipo → html guardado
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [vista, setVista]           = useState('editor'); // 'editor' | 'preview'

  const tipoInfo = TIPOS_PLANTILLA.find(t => t.tipo === tipoActivo);
  const htmlActual = htmls[tipoActivo] ?? PLANTILLAS_DEFAULT[tipoActivo]?.html ?? '';
  const dirty = htmlActual !== (saved[tipoActivo] ?? PLANTILLAS_DEFAULT[tipoActivo]?.html ?? '');

  // Cargar todas las plantillas guardadas en DB
  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        const res = await api.get('/plantillas/index.php');
        const rows = res.data ?? [];
        const porTipo = {};
        rows.forEach(r => { porTipo[r.tipo] = r.html; });
        // Merge DB rows con defaults
        const inicialHtmls = {};
        const inicialSaved = {};
        TIPOS_PLANTILLA.forEach(({ tipo }) => {
          inicialHtmls[tipo] = porTipo[tipo] ?? PLANTILLAS_DEFAULT[tipo]?.html ?? '';
          inicialSaved[tipo] = porTipo[tipo] ?? PLANTILLAS_DEFAULT[tipo]?.html ?? '';
        });
        setHtmls(inicialHtmls);
        setSaved(inicialSaved);
      } catch {
        const inicialHtmls = {};
        TIPOS_PLANTILLA.forEach(({ tipo }) => {
          inicialHtmls[tipo] = PLANTILLAS_DEFAULT[tipo]?.html ?? '';
        });
        setHtmls(inicialHtmls);
        setSaved({ ...inicialHtmls });
      }
      setLoading(false);
    };
    cargar();
  }, []);

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const label = PLANTILLAS_DEFAULT[tipoActivo]?.nombre ?? tipoActivo;
      await api.put('/plantillas/index.php', {
        tipo: tipoActivo,
        nombre: label,
        html: htmlActual,
      });
      setSaved(prev => ({ ...prev, [tipoActivo]: htmlActual }));
      toast.success('Plantilla guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    const def = PLANTILLAS_DEFAULT[tipoActivo]?.html ?? '';
    setHtmls(prev => ({ ...prev, [tipoActivo]: def }));
  };

  const handlePrintPreview = () => {
    const rendered = render(htmlActual, DATOS_MUESTRA[tipoActivo] ?? {});
    printDocument(rendered);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] gap-4">
      {/* ── Header ── */}
      <div className="page-header shrink-0">
        <h1 className="page-title">Plantillas de impresión</h1>
        <div className="flex items-center gap-2">
          <button
            className="btn-ghost text-sm gap-1.5"
            onClick={handlePrintPreview}
            title="Imprimir con datos de muestra"
          >
            <Printer size={15} /> Probar impresión
          </button>
          {canEdit && (
            <>
              <button
                className="btn-ghost text-sm gap-1.5 text-slate-400"
                onClick={handleReset}
                disabled={saving}
                title="Restaurar plantilla original"
              >
                <RotateCcw size={14} /> Restaurar
              </button>
              <button
                className="btn-primary gap-1.5"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                <Save size={15} /> {saving ? 'Guardando...' : dirty ? 'Guardar cambios' : 'Guardado'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* ── Sidebar — tipo selector ── */}
        <div className="w-52 shrink-0 space-y-1">
          {TIPOS_PLANTILLA.map(({ tipo, label }) => {
            const esDirty = (htmls[tipo] ?? '') !== (saved[tipo] ?? PLANTILLAS_DEFAULT[tipo]?.html ?? '');
            return (
              <button
                key={tipo}
                onClick={() => setTipoActivo(tipo)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all text-left
                  ${tipoActivo === tipo
                    ? 'bg-primary-500 text-white shadow-sm shadow-primary-500/20'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
              >
                <FileText size={14} className="shrink-0" />
                <span className="flex-1 leading-tight">{label}</span>
                {esDirty && (
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tipoActivo === tipo ? 'bg-white/70' : 'bg-amber-400'}`} />
                )}
                {tipoActivo === tipo && <ChevronRight size={13} className="shrink-0 opacity-60" />}
              </button>
            );
          })}

          {/* Variables panel */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1 mb-2">Variables disponibles</p>
            <div className="flex flex-wrap gap-1.5 px-1">
              {tipoInfo?.variables.map(v => <VarPill key={v} name={v} />)}
            </div>
            <p className="text-[10px] text-slate-400 mt-3 px-1 leading-relaxed">
              Las variables en naranja son listas: usá <code className="bg-slate-100 dark:bg-slate-700 px-0.5 rounded">{'{{#each}}'}</code> para iterar.
            </p>
          </div>
        </div>

        {/* ── Panel principal ── */}
        <div className="flex-1 flex flex-col min-w-0 card !p-0 overflow-hidden">
          {/* Tab bar */}
          <div className="flex items-center gap-0 border-b border-slate-200 dark:border-slate-700 px-4 shrink-0">
            {[
              { key: 'editor',  label: 'Editor HTML', Icon: Code2 },
              { key: 'preview', label: 'Vista previa', Icon: Eye  },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setVista(key)}
                className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 -mb-px transition-colors
                  ${vista === key
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0">
            {vista === 'editor' ? (
              <Editor
                height="100%"
                defaultLanguage="html"
                language="html"
                value={htmlActual}
                onChange={val => {
                  if (!canEdit) return;
                  setHtmls(prev => ({ ...prev, [tipoActivo]: val ?? '' }));
                }}
                options={{
                  readOnly: !canEdit,
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineHeight: 20,
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  formatOnPaste: true,
                  theme: document.documentElement.classList.contains('dark') ? 'vs-dark' : 'vs',
                }}
                theme={document.documentElement.classList.contains('dark') ? 'vs-dark' : 'vs'}
              />
            ) : (
              <Preview html={htmlActual} tipo={tipoActivo} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
